import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';

function employeeInitials(firstName: string, lastName?: string) {
  return `${firstName[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

interface HrEmployeeProfileHeaderProps {
  firstName: string;
  lastName?: string;
  employeeCode: string;
  email?: string;
  subtitle?: string;
  badges?: { label: string; variant?: 'default' | 'success' | 'warning' | 'error' | 'info' }[];
  actions?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function HrEmployeeProfileHeader({
  firstName,
  lastName,
  employeeCode,
  email,
  subtitle,
  badges,
  actions,
  compact,
  className,
}: HrEmployeeProfileHeaderProps) {
  const fullName = `${firstName} ${lastName ?? ''}`.trim();
  const initials = employeeInitials(firstName, lastName);

  return (
    <div
      className={cn(
        'card flex flex-wrap items-start justify-between gap-4',
        compact ? 'p-4' : 'p-5',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-4">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 font-semibold text-white shadow-sm',
            compact ? 'h-12 w-12 text-sm' : 'h-14 w-14 text-base',
          )}
          aria-hidden
        >
          {initials}
        </div>
        <div className="min-w-0">
          <h2 className={cn('font-semibold text-slate-900 dark:text-slate-100', compact ? 'text-lg' : 'text-xl')}>
            {fullName}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            <span className="font-medium text-slate-700 dark:text-slate-300">{employeeCode}</span>
            {email ? (
              <>
                <span aria-hidden> · </span>
                <a href={`mailto:${email}`} className="text-link">
                  {email}
                </a>
              </>
            ) : null}
          </p>
          {subtitle && <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
          {badges && badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {badges.map((b) => (
                <Badge key={b.label} variant={b.variant ?? 'default'}>
                  {b.label}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export { employeeInitials };
